import { Clipboard, Headphones, DollarSign, Users, Heart, Briefcase, Monitor, Database, Search, Archive } from 'lucide-react';

export const services = [
  {
    id: 'administrative-support',
    slug: 'administrative-support',
    title: 'Administrative Support',
    shortTitle: 'Admin Support',
    tagline: 'Operational precision for executive focus',
    icon: Clipboard,
    color: '#0E5A57',
    description: 'Comprehensive administrative support that keeps your operations running with precision, allowing your leadership team to focus on what matters most — growing the business.',
    challenges: [
      'Executives spending 30%+ of their time on administrative tasks',
      'Inconsistent documentation and filing practices',
      'Communication backlogs causing missed opportunities',
      'Lack of operational continuity during team changes',
    ],
    scope: [
      'Email management and correspondence handling',
      'Calendar and schedule coordination',
      'Document preparation, formatting, and filing',
      'Travel coordination and logistics',
      'Meeting preparation and minutes',
      'Supplier and vendor communication',
      'Reporting and administrative tracking',
    ],
    benefits: [
      'Reclaim 10-15 hours per week for leadership focus',
      'Consistent, professional communication standards',
      'Operational continuity regardless of internal changes',
      'Reduced overhead vs. in-house administrative staff',
    ],
    process: [
      { step: '01', title: 'Discovery', desc: 'We map your current administrative workflows and identify gaps.' },
      { step: '02', title: 'Handover Design', desc: 'We design a structured handover process for each function.' },
      { step: '03', title: 'Integration', desc: 'Seamless integration with your systems and communication tools.' },
      { step: '04', title: 'Quality Review', desc: 'Regular reviews to ensure standards are maintained.' },
    ],
    faqs: [
      { q: 'How quickly can you start?', a: 'After an initial discovery call and onboarding session, we typically begin within 3-5 business days.' },
      { q: 'Do you work across time zones?', a: 'Yes. We support clients across the UK, US, Canada, Europe, Australia, and New Zealand.' },
      { q: 'What communication tools do you use?', a: 'We adapt to your preferred tools — email, Slack, Teams, or any CRM/PM platform you use.' },
    ],
    seoTitle: 'Administrative Support Services | NKP VENTURES',
    seoDesc: 'Professional administrative support services for international businesses. Email management, scheduling, documentation, and more.',
  },
  {
    id: 'customer-support',
    slug: 'customer-support',
    title: 'Customer Support',
    shortTitle: 'Customer Support',
    tagline: 'Every client interaction, handled with excellence',
    icon: Headphones,
    color: '#2E7D7A',
    description: 'Professional customer support that represents your brand with the same care and standards you would expect from your best in-house team member.',
    challenges: [
      'Growing support volume outpacing internal team capacity',
      'Inconsistent response quality across different agents',
      'Extended response times damaging client satisfaction',
      'High cost of maintaining in-house support teams',
    ],
    scope: [
      'Email and ticket-based customer support',
      'Live chat support management',
      'Customer query resolution and escalation',
      'Knowledge base management',
      'Customer feedback collection and reporting',
      'Onboarding support for new clients',
      'Returns, complaints, and dispute management',
    ],
    benefits: [
      'Consistent, professional response quality across all interactions',
      'Faster response times with structured workflows',
      'Scalable capacity for peak periods',
      'Reduced cost per support interaction',
    ],
    process: [
      { step: '01', title: 'Brand Alignment', desc: 'We learn your brand voice, standards, and common scenarios.' },
      { step: '02', title: 'Knowledge Build', desc: 'We build a comprehensive knowledge base for your products/services.' },
      { step: '03', title: 'Process Design', desc: 'Structured workflows for common queries and escalation paths.' },
      { step: '04', title: 'Quality Monitoring', desc: 'Regular quality sampling and continuous improvement.' },
    ],
    faqs: [
      { q: 'Will customers know support is outsourced?', a: 'Only if you want them to. We operate as a seamless extension of your team using your brand voice and identity.' },
      { q: 'Can you handle complex technical queries?', a: 'We handle first-line support and intelligent triage. Complex technical queries are escalated to your internal teams with full context.' },
      { q: 'What platforms do you support?', a: 'Zendesk, Freshdesk, Intercom, HubSpot, email, and most CRM platforms.' },
    ],
    seoTitle: 'Customer Support Outsourcing | NKP VENTURES',
    seoDesc: 'Professional customer support outsourcing services. Email support, live chat, and ticket management for growing businesses.',
  },
  {
    id: 'finance-accounting',
    slug: 'finance-accounting',
    title: 'Finance & Accounting',
    shortTitle: 'Finance & Accounting',
    tagline: 'Financial clarity for confident decision-making',
    icon: DollarSign,
    color: '#153E5C',
    description: 'Structured finance and accounting support that keeps your books accurate, your reporting timely, and your financial operations running smoothly.',
    challenges: [
      'Delayed financial reporting obscuring business performance',
      'Bookkeeping backlogs creating compliance risk',
      'High cost of dedicated in-house finance staff',
      'Manual reconciliation processes prone to errors',
    ],
    scope: [
      'Accounts payable and receivable management',
      'Bookkeeping and ledger maintenance',
      'Invoice processing and reconciliation',
      'Expense reporting and management',
      'Financial reporting support',
      'Bank reconciliation',
      'Payroll data preparation support',
    ],
    benefits: [
      'Accurate, up-to-date financial records',
      'Reduced risk of compliance issues',
      'Cost savings versus dedicated finance staff',
      'Timely reporting for better decision-making',
    ],
    process: [
      { step: '01', title: 'Financial Audit', desc: 'Review of current bookkeeping systems and data quality.' },
      { step: '02', title: 'System Integration', desc: 'Secure access to your accounting platform (Xero, QuickBooks, etc.).' },
      { step: '03', title: 'Process Standardisation', desc: 'Defined workflows for each financial function.' },
      { step: '04', title: 'Regular Review', desc: 'Monthly financial summaries and ongoing reconciliation.' },
    ],
    faqs: [
      { q: 'Which accounting platforms do you support?', a: 'Xero, QuickBooks, FreshBooks, Sage, and most major platforms.' },
      { q: 'Is my financial data secure?', a: 'Yes. All financial data is handled under strict confidentiality agreements with controlled access protocols.' },
      { q: 'Do you file tax returns?', a: 'We provide financial records and reports to support your accountant or tax advisor. We do not provide regulated financial advice.' },
    ],
    seoTitle: 'Finance & Accounting Support | NKP VENTURES',
    seoDesc: 'Bookkeeping, accounts payable/receivable, and financial reporting support for businesses across the UK, US, and beyond.',
  },
  {
    id: 'human-resources',
    slug: 'human-resources',
    title: 'Human Resources Support',
    shortTitle: 'HR Support',
    tagline: 'People operations, handled with professionalism',
    icon: Users,
    color: '#0E5A57',
    description: 'Comprehensive HR support services that manage the administrative and operational dimensions of human resources so your team can focus on people, not paperwork.',
    challenges: [
      'HR administrative tasks consuming management time',
      'Inconsistent employee documentation and record-keeping',
      'Onboarding processes causing poor first impressions',
      'High cost of dedicated HR staff for smaller businesses',
    ],
    scope: [
      'Employee record management and documentation',
      'Onboarding coordination and documentation',
      'Job posting and candidate correspondence',
      'Leave and attendance tracking',
      'HR policy documentation support',
      'Performance review coordination',
      'HR reporting and analytics support',
    ],
    benefits: [
      'Professional, consistent HR administration',
      'Faster, smoother employee onboarding',
      'Reduced management time on HR paperwork',
      'Improved HR record quality and compliance readiness',
    ],
    process: [
      { step: '01', title: 'HR Audit', desc: 'Review of current HR processes, documentation, and gaps.' },
      { step: '02', title: 'Process Design', desc: 'Standardised workflows for each HR function.' },
      { step: '03', title: 'Systems Setup', desc: 'Integration with your HR tools and communication platforms.' },
      { step: '04', title: 'Ongoing Management', desc: 'Regular HR operations support with escalation pathways.' },
    ],
    faqs: [
      { q: 'Do you provide HR advice or consulting?', a: 'We provide HR administrative and operational support. For legal HR advice, we work alongside your HR advisor or solicitor.' },
      { q: 'Can you manage recruitment processes?', a: 'We manage the administrative dimensions of recruitment — job postings, candidate correspondence, interview coordination. Final decisions remain with your team.' },
      { q: 'How is employee data protected?', a: 'All employee data is handled under strict confidentiality agreements with role-based access controls.' },
    ],
    seoTitle: 'HR Support Services | NKP VENTURES',
    seoDesc: 'Professional HR administrative support — employee records, onboarding, recruitment coordination, and HR reporting.',
  },
  {
    id: 'healthcare-administration',
    slug: 'healthcare-administration',
    title: 'Healthcare Administration',
    shortTitle: 'Healthcare Admin',
    tagline: 'Supporting healthcare operations with precision',
    icon: Heart,
    color: '#2E7D7A',
    description: 'Specialist healthcare administrative support that understands the operational context, compliance obligations, and professional standards of healthcare organisations.',
    challenges: [
      'Clinical staff spending excessive time on administrative tasks',
      'Patient communication workflows creating backlogs',
      'Documentation management gaps creating compliance risk',
      'High administrative overhead reducing care capacity',
    ],
    scope: [
      'Patient communication and appointment coordination',
      'Medical documentation and records management',
      'Administrative correspondence handling',
      'Billing support and invoice processing',
      'Data entry and form processing',
      'Reporting and operational tracking',
      'General back-office administration',
    ],
    benefits: [
      'Reduced administrative burden on clinical staff',
      'More consistent, professional patient communication',
      'Improved documentation quality and completeness',
      'Lower administrative overhead costs',
    ],
    process: [
      { step: '01', title: 'Compliance Review', desc: 'Understanding your jurisdiction\'s requirements and protocols.' },
      { step: '02', title: 'Process Mapping', desc: 'Mapping all administrative workflows and communication touchpoints.' },
      { step: '03', title: 'Confidential Handover', desc: 'Structured handover with data security protocols in place.' },
      { step: '04', title: 'Quality Assurance', desc: 'Regular audits to maintain quality and compliance standards.' },
    ],
    faqs: [
      { q: 'Do you understand healthcare compliance requirements?', a: 'Yes. We are experienced in supporting UK and international healthcare organisations with appropriate confidentiality and compliance standards.' },
      { q: 'How is patient data handled?', a: 'Patient-adjacent data is handled under strict confidentiality protocols with controlled access and no third-party sharing.' },
      { q: 'Can you support both NHS and private healthcare?', a: 'Yes. We support both NHS-affiliated and private healthcare organisations across the UK and internationally.' },
    ],
    seoTitle: 'Healthcare Administration Support | NKP VENTURES',
    seoDesc: 'Specialist healthcare administrative support — patient communication, records management, and back-office operations.',
  },
  {
    id: 'business-operations',
    slug: 'business-operations',
    title: 'Business Operations Support',
    shortTitle: 'Business Operations',
    tagline: 'The operational backbone your business needs',
    icon: Briefcase,
    color: '#153E5C',
    description: 'End-to-end business operations support that manages the day-to-day operational functions keeping your business running, coordinated, and scalable.',
    challenges: [
      'Operational tasks consuming leadership time and focus',
      'Coordination gaps causing delays and missed deadlines',
      'Growing operational complexity with limited internal capacity',
      'Lack of documented processes creating single points of failure',
    ],
    scope: [
      'Operational workflow management',
      'Project coordination and tracking',
      'Vendor and supplier management',
      'Contract administration',
      'Operational reporting',
      'Process documentation and SOP creation',
      'Cross-functional coordination support',
    ],
    benefits: [
      'Smoother daily operations with fewer coordination gaps',
      'Leadership time freed for strategic priorities',
      'Documented, consistent operational processes',
      'Scalable operational capacity as you grow',
    ],
    process: [
      { step: '01', title: 'Operational Review', desc: 'Comprehensive review of current operational functions and gaps.' },
      { step: '02', title: 'Scope Definition', desc: 'Clear definition of which functions will be supported.' },
      { step: '03', title: 'Process Design', desc: 'Documented workflows and communication protocols.' },
      { step: '04', title: 'Embedded Support', desc: 'Ongoing operational support with regular performance reviews.' },
    ],
    faqs: [
      { q: 'How is your team integrated with ours?', a: 'We embed into your communication tools and workflows, operating as a transparent extension of your team.' },
      { q: 'Can you manage external vendors on our behalf?', a: 'Yes. We manage vendor correspondence, follow-ups, contract administration, and tracking on behalf of your business.' },
      { q: 'What if we need to scale up quickly?', a: 'Our capacity scales with your operational needs without the delays of internal hiring.' },
    ],
    seoTitle: 'Business Operations Support | NKP VENTURES',
    seoDesc: 'End-to-end business operations support — project coordination, vendor management, process documentation, and operational reporting.',
  },
  {
    id: 'virtual-assistance',
    slug: 'virtual-assistance',
    title: 'Virtual Assistance',
    shortTitle: 'Virtual Assistance',
    tagline: 'A dedicated professional in your corner',
    icon: Monitor,
    color: '#0E5A57',
    description: 'Dedicated virtual assistance that provides the support of a highly capable professional without the overhead of full-time employment.',
    challenges: [
      'Executives lacking dedicated support for day-to-day tasks',
      'High cost and complexity of hiring full-time executive assistants',
      'Inconsistent task execution without dedicated support',
      'Personal and professional life becoming operationally disorganised',
    ],
    scope: [
      'Personal and executive scheduling',
      'Email and correspondence management',
      'Research and information gathering',
      'Document and presentation preparation',
      'Online errands and bookings',
      'Liaison and coordination tasks',
      'Personal project management support',
    ],
    benefits: [
      'Consistent, reliable support without full-time employment complexity',
      'More organised, focused executive life',
      'Professional representation in communications',
      'Flexible support that scales with your needs',
    ],
    process: [
      { step: '01', title: 'Preferences Discovery', desc: 'Deep understanding of your working style, priorities, and preferences.' },
      { step: '02', title: 'Systems Integration', desc: 'Access to your calendar, email, and task management tools.' },
      { step: '03', title: 'Trial Period', desc: 'An initial period to align working rhythms and communication patterns.' },
      { step: '04', title: 'Ongoing Partnership', desc: 'A long-term relationship built on understanding and trust.' },
    ],
    faqs: [
      { q: 'Will I have a dedicated assistant?', a: 'Yes. You will work with a dedicated assistant who learns your preferences and represents you consistently.' },
      { q: 'What hours are you available?', a: 'We align our availability with your time zone and working hours, covering your core business hours.' },
      { q: 'How quickly can a VA start?', a: 'After an initial discovery call, your dedicated VA can begin within 2-3 business days.' },
    ],
    seoTitle: 'Virtual Assistant Services | NKP VENTURES',
    seoDesc: 'Dedicated virtual assistance for executives and business owners. Scheduling, email management, research, and more.',
  },
  {
    id: 'crm-support',
    slug: 'crm-support',
    title: 'CRM Support',
    shortTitle: 'CRM Support',
    tagline: 'Your client relationships, expertly managed',
    icon: Database,
    color: '#2E7D7A',
    description: 'Professional CRM management and customer relationship operations that keep your pipeline clean, your data accurate, and your client communications timely.',
    challenges: [
      'CRM data becoming outdated, incomplete, or inaccurate',
      'Sales team spending time on data entry instead of selling',
      'Poor pipeline visibility due to inconsistent CRM usage',
      'Client follow-up slipping through the cracks',
    ],
    scope: [
      'CRM data entry and maintenance',
      'Contact database management and enrichment',
      'Pipeline tracking and reporting',
      'Client follow-up coordination',
      'CRM workflow configuration support',
      'Reporting and analytics preparation',
      'Data cleaning and deduplication',
    ],
    benefits: [
      'Accurate, up-to-date CRM data at all times',
      'Better pipeline visibility for sales leadership',
      'More consistent client follow-up and communication',
      'Sales team freed for selling, not data management',
    ],
    process: [
      { step: '01', title: 'CRM Audit', desc: 'Review of current CRM setup, data quality, and usage patterns.' },
      { step: '02', title: 'Workflow Design', desc: 'Optimised workflows for data entry, updates, and reporting.' },
      { step: '03', title: 'Access Setup', desc: 'Secure CRM access with appropriate permission levels.' },
      { step: '04', title: 'Ongoing Maintenance', desc: 'Daily data maintenance and weekly reporting support.' },
    ],
    faqs: [
      { q: 'Which CRM platforms do you support?', a: 'HubSpot, Salesforce, Pipedrive, Zoho, and most major CRM platforms.' },
      { q: 'Can you configure our CRM workflows?', a: 'We provide CRM data and process support. For complex technical CRM configuration, we work alongside your CRM administrator.' },
      { q: 'How do you handle sensitive client data?', a: 'All client data is handled under strict confidentiality agreements with role-based access and no third-party sharing.' },
    ],
    seoTitle: 'CRM Support Services | NKP VENTURES',
    seoDesc: 'Professional CRM data management — contact maintenance, pipeline tracking, and client relationship operations.',
  },
  {
    id: 'business-research',
    slug: 'business-research',
    title: 'Business Research',
    shortTitle: 'Business Research',
    tagline: 'Intelligence that drives better decisions',
    icon: Search,
    color: '#153E5C',
    description: 'Structured business and market research support that provides the intelligence your team needs to make better strategic, commercial, and operational decisions.',
    challenges: [
      'Leadership making decisions without adequate market intelligence',
      'Research projects taking too long due to limited internal capacity',
      'Inconsistent research quality and documentation standards',
      'Competitor and market monitoring not being done systematically',
    ],
    scope: [
      'Market research and industry analysis',
      'Competitor intelligence and benchmarking',
      'Prospect research and list building',
      'Industry and regulatory research',
      'Due diligence support',
      'Data gathering and summarisation',
      'Research report preparation',
    ],
    benefits: [
      'Better-informed strategic and commercial decisions',
      'Competitive intelligence delivered systematically',
      'Research capacity without full-time research staff',
      'Structured, professional research deliverables',
    ],
    process: [
      { step: '01', title: 'Brief Definition', desc: 'Clear definition of research objectives, scope, and deliverable format.' },
      { step: '02', title: 'Source Strategy', desc: 'Identification of authoritative sources and research methodologies.' },
      { step: '03', title: 'Research Execution', desc: 'Systematic gathering, verification, and organisation of findings.' },
      { step: '04', title: 'Delivery & Review', desc: 'Structured report delivery with a review and Q&A session.' },
    ],
    faqs: [
      { q: 'How quickly can research projects be delivered?', a: 'Turnaround depends on scope. Most focused research projects are delivered within 2-5 business days.' },
      { q: 'Do you provide analysis or just data collection?', a: 'We provide both data collection and structured analysis with clear summaries and insights.' },
      { q: 'Can you build prospect lists for sales?', a: 'Yes. We build targeted, accurate prospect lists based on your ideal customer profile and criteria.' },
    ],
    seoTitle: 'Business Research Services | NKP VENTURES',
    seoDesc: 'Market research, competitor intelligence, and business research services for strategic decision-making.',
  },
  {
    id: 'back-office-operations',
    slug: 'back-office-operations',
    title: 'Back Office Operations',
    shortTitle: 'Back Office',
    tagline: 'The operational engine behind your business',
    icon: Archive,
    color: '#0E5A57',
    description: 'Comprehensive back office operations support that manages the essential behind-the-scenes functions keeping your business organised, compliant, and operationally ready.',
    challenges: [
      'Back office volume growing faster than internal team capacity',
      'High error rates in manual data processing and documentation',
      'Compliance documentation not being maintained consistently',
      'Internal teams overwhelmed by routine operational tasks',
    ],
    scope: [
      'Data entry and processing',
      'Document management and filing',
      'Order processing support',
      'Invoice and billing processing',
      'Compliance documentation management',
      'Records management and archiving',
      'Operational reporting and dashboards',
    ],
    benefits: [
      'Accurate, timely back office processing',
      'Reduced operational error rates',
      'Scalable capacity for growing business volumes',
      'Internal teams focused on higher-value activities',
    ],
    process: [
      { step: '01', title: 'Volume Assessment', desc: 'Assessment of current back office volumes, types, and quality requirements.' },
      { step: '02', title: 'Quality Standards', desc: 'Definition of accuracy standards and quality control protocols.' },
      { step: '03', title: 'Process Integration', desc: 'Integration with your systems and established workflows.' },
      { step: '04', title: 'Performance Monitoring', desc: 'Regular performance reviews with accuracy and volume metrics.' },
    ],
    faqs: [
      { q: 'Can you handle high-volume data entry?', a: 'Yes. We have structured processes for high-volume, accuracy-critical data processing.' },
      { q: 'How do you ensure accuracy?', a: 'We apply double-checking protocols, quality sampling, and regular accuracy reviews for all processing work.' },
      { q: 'Can you scale up during peak periods?', a: 'Yes. We can increase capacity quickly during peak periods without the delays of internal hiring.' },
    ],
    seoTitle: 'Back Office Operations Support | NKP VENTURES',
    seoDesc: 'Comprehensive back office support — data entry, document management, order processing, and operational reporting.',
  },
];

export const getServiceBySlug = (slug) => services.find(s => s.slug === slug);
